import { gql } from "@apollo/client";

import { PATIENT_ORDERS_IN_PROGRESS_FIELDS } from "@/components/patientView/testResults/graphql";

export const PATIENT_ENTITLEMENTS_QUERY = gql`
  query PatientEntitlementsQuery($userId: ID!, $organisationId: ID!) {
    user(userId: $userId, organisationId: $organisationId) {
      id
      entitlements {
        key
        granted
      }
    }
  }
`;

export const REPORT_FIELDS = gql`
  fragment ReportFields on ReportType {
    id
    name
    slug
    questionsAndAnswersCount (userId: $userId, organisationId: $organisationId)
    reportInformationPanels {
      id
      title
      body
      diagram
      sortOrder
      pdf
    }
    purchaseProduct {
      id
      name
      status
      content {
        description
        shortDescription
        reportIcon
        informationPanels {
          id
          title
          body
          displayContext
        }
      }
      functionalDatapointCount
      dnaDatapointCount
      grantedEntitlements {
        key
      }
      paymentIsoCode
      organisation
      isCyrex
      tradeFullPrice
      tradeCurrentPrice
      exvatFullPrice
    }
  }
`;

export const USER_REPORT_FIELDS = gql`
  fragment UserReportFields on UserReportType {
    id
    name
    slug
    publicationStatus
    publishedViews
    showNewTestMarkersNotification (organisationId: $organisationId)
  }
`;

export const REPORTS_LIST_QUERY = gql`
  query ReportsListQuery($userId: ID!, $organisationId: ID!, $userTestId: ID) {
    reports (userId: $userId, organisationId: $organisationId, userTestId: $userTestId) {
      ...ReportFields
      functionalDatapointCount
      dnaDatapointCount
    }
    userReports (userId: $userId, organisationId: $organisationId) {
      id
      publicationStatus
      subsector {
        id
        name
        slug
        purchaseProduct {
          id
          paymentIsoCode
          organisation
          content {
            reportIcon
          }
          grantedEntitlements {
            key
          }
        }
      }
      functionalDatapointCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      dnaDatapointCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      questionsAndAnswersCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
    }
  }
  ${REPORT_FIELDS}
`;

export const REPORT_DETAIL_QUERY = gql`
  query ReportDetailQuery($subsectorSlug: String!, $organisationId: ID!, $userId: ID!) {
    report(slug: $subsectorSlug, userId: $userId, organisationId: $organisationId) {
      ...ReportFields
      assessedDatapoints(userId: $userId, organisationId: $organisationId) {
        id
        name
        isAvailable
        testProducts (organisationId: $organisationId) {
          id
          name
          paymentIsoCode
          organisation
          productFamily {
            id
            name
          }
        }
      }
      questionsAndAnswers(userId: $userId, organisationId: $organisationId) {
        id
        question {
          id
          symptomGroup
        }
        answerScore
        answerText
      }
    }
    userReport(subsectorSlug: $subsectorSlug, organisationId: $organisationId, userId: $userId) {
      ...UserReportFields
      dnaDatapointCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      functionalDatapointCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      questionsAndAnswersCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      snpsForDiagram
      expectedSnps
      dnaDatapointsOutOfRange {
        id
        riskAllele(useSnpValue: true)
        value
        impactActivity
        snpInterpretation
        snpSuggestion
        datapoint {
          id
          name
        }
      }
      functionalDatapointsOutOfRange {
        id
        value
        sampleType
        dateExtracted
        chartConfiguration {
          interpretation
        }
        datapoint {
          id
          name
          units
        }
      }
      questionsAndAnswersOutOfRange {
        id
        question {
          id
          symptomGroup
        }
        answerScore
        answerText
      }
      subsector {
        id
        name
        slug
        reportInformationPanels {
          id
          title
          body
          diagram
        }
      }
    }
  }
  ${REPORT_FIELDS}
  ${USER_REPORT_FIELDS}
`;

export const REPORT_RESULTS_QUERY = gql`
  query ReportResultsQuery(
    $subsectorSlug: String!
    $organisationId: ID!
    $userId: ID!
    $first: Int!
    $after: String!
  ) {
    userReport(subsectorSlug: $subsectorSlug, organisationId: $organisationId, userId: $userId) {
      id
      dnaDatapointCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      functionalDatapointCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      questionsAndAnswersCounts {
        inRangeCount
        outOfRangeCount
        untestedCount
      }
      userResultConnections(after: $after, first: $first) {
        edges {
          cursor
          node {
            id
            dnaDatapointCounts {
              inRangeCount
              outOfRangeCount
              untestedCount
            }
            functionalDatapointCounts {
              inRangeCount
              outOfRangeCount
              untestedCount
            }
            symptomCounts {
              inRangeCount
              outOfRangeCount
              untestedCount
            }
            result {
              id
              name
              description
              diagram
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

export const REPORT_RELATED_TESTS_QUERY = gql`
  query PatientReportRelatedTestsQuery(
    $userId: ID!
    $organisationId: ID!
    $report: String!
    $allTests: Boolean!
    $includeUnapproved: Boolean!
  ) {
    userTests(
      userId: $userId
      organisationId: $organisationId
      report: $report
      allTests: $allTests
      includeUnapproved: $includeUnapproved
    ) {
      id
      name
      reviewStatus
      externalSourceTest
      createdDateFormatted
      displayOrderId
    }
    ordersInProgress(userId: $userId, organisationId: $organisationId, report: $report) {
      ...PatientOrdersInProgressFields
    }
  }
  ${PATIENT_ORDERS_IN_PROGRESS_FIELDS}
`;

export const RELEASE_USER_REPORT_MUTATION = gql`
  mutation ReleaseUserReportMutation(
    $organisationId: ID!
    $userId: ID!
    $reportSlug: String!
    $overview: Boolean
    $summary: Boolean
    $interpretation: Boolean
    $suggestions: Boolean
  ) {
    releaseUserReportMutation(
      organisationId: $organisationId
      userId: $userId
      reportSlug: $reportSlug
      overview: $overview
      summary: $summary
      interpretation: $interpretation
      suggestions: $suggestions
    ) {
      ok
      userReport {
        id
        publicationStatus
        publishedViews
      }
    }
  }
`;


export const USER_RESULT_INTERPRETATION_DETAILS_QUERY = gql`
  query UserResultInterpretationDetailsQuery($userResultId: ID!, $userId: ID!, $organisationId: ID!) {
    userResult(userResultId: $userResultId, userId: $userId, organisationId: $organisationId) {
      id
      snpsForDiagram
      expectedSnps
      questionsAndAnswers {
        id
        question {
          id
          symptomGroup
        }
        answerText
        answerScore
      }
      dnaDatapoints {
        id
        interpretation
        riskAllele(useSnpValue: true)
        value
        impactActivity
        snpInterpretation
        snpSuggestion
        datapoint {
          id
          name
        }
      }
      untestedDnaDatapoints(organisationId: $organisationId, userId: $userId) {
        id
        name
      }
      untestedDatapoints (organisationId: $organisationId, userId: $userId) {
        id
        name
        advertisedTestProductName
        advertisedTestProductSlug
      }
      functionalDatapoints {
        id
        value
        sampleType
        dateExtracted
        chartConfiguration {
          interpretation
        }
        datapoint {
          id
          name
          units
        }
      }
    }
  }
`;